/**
 * A modal allowing users to select a source and target language for translation of the work.
 * Premium users receive the translatfed document.
 * Free users are upsold the translation product.
 */
import React, { Component } from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';

import { Icon } from 'js/design_system/Icon';
import ModalBase from 'js/components/modals/Modal';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { Work } from 'js/liswp/types';
import $image_url from 'js/utils/image_url';
import LanguageDropdown from './LanguageDropdown';
import TranslationFeaturesText from './TranslationFeaturesText';
import TranslateButton from './TranslateButton';
import {
  downloadTranslation,
  LanguageCode,
  navigateToCheckout,
  getPreview,
  PreviewResponse,
} from './util';
import { abTest as translationModalUpsellAbTest } from 'js/ab_tests/tests/translation_modal_upsell_with_abstract';
import trackClick from 'js/utils/trackClick';

const SMALL_SCREEN_SIZE = 800;
const TINY_SCREEN_SIZE = 630;

const Modal = styled(ModalBase)`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  background-color: #ffffff;
  box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.5);
  max-height: 100%;
  padding: 0;
  position: relative;

  @media (min-width: ${TINY_SCREEN_SIZE + 1}px) {
    max-width: 750px;
  }

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    width: 100%;
  }

  @media (max-width: 430px) {
    height: 100%;
    max-height: none;
    max-width: none;
    border-radius: 0;
  }

  @media (min-width: 431px) {
    place-self: start center;
    margin-top: 25px;
  }
`;

const X = styled.i`
  position: absolute;
  right: 24px;
  top: 20px;
  color: #7a7a7a;
  font-size: 20px;
  cursor: pointer;

  @media (max-width: ${SMALL_SCREEN_SIZE}px) {
    right: 16px;
    top: 14px;
  }
`;

const TopSelectionContainer = styled.div<{ button: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 60px 50px 50px 50px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    margin: 40px 16px 0px 16px;
  }

  &.button {
    margin-bottom: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  margin-bottom: 24px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    justify-content: center;
  }
`;

const Title = styled.h1`
  font-family: Roboto;
  font-style: normal;
  font-weight: bold;
  font-size: 32px;
  line-height: 37px;
  letter-spacing: -0.01em;
  color: #222222;
  margin: 0;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    font-size: 28px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-evenly;

  @media (min-width: ${TINY_SCREEN_SIZE + 1}px) {
    min-width: 532px;
  }

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    flex-wrap: wrap;
  }
`;

const LeftSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    width: 100%;
  }
`;

const IconContainer = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
`;

const RightSelectionContainer = styled.div`
  display: flex;
  align-items: flex-start;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    width: 100%;
  }
`;

const SubText = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: -0.01em;
  color: #888888;
  margin-top: 6px;
  margin-bottom: 8px;
`;

const UpsellContainer = styled.div`
  background-color: #f4f8ff;
  padding: 32px 50px 32px 50px;
  border-radius: 0 0 4px 4px;
  display: flex;
  flex-direction: column;
  max-width: 632px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    padding: 32px 16px 40px 16px;
  }
`;

const PreviewUpsellContainer = styled.div`
  background-color: #cdd6e6;
  margin: 24px 50px 40px 50px;
  padding: 36px 36px 24px 36px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.1);

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    margin: 20px 0 0 0;
    padding: 36px 16px 24px 16px;
    box-shadow: 0px -4px 10px rgba(0, 0, 0, 0.1);
    border-radius: 12px 12px 4px 4px;
  }
`;

const PreviewContainer = styled.div`
  display: flex;
  margin-bottom: 28px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    margin-bottom: 16px;
  }
`;

const PreviewPDFImageContainer = styled.div`
  display: flex;
  position: relative;
  margin-right: 30px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    margin-right: 20px;
  }
`;

const PreviewFlag = styled.div`
  display: flex;
  position: absolute;
  top: 12px;
  right: -10px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    right: -7px;
  }
`;

const PreviewFlagImage = styled.img`
  width: 40px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    width: 30px;
  }
`;

const PreviewTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 300px;
`;

const PreviewAbstractTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 532px;
  margin-bottom: 12px;
`;

const PreviewTitle = styled.div`
  font-family: Georgia;
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 130%;

  letter-spacing: -0.01em;
  margin-bottom: 12px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    max-height: 128px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const PreviewTranslatedPageCountLine = styled.div`
  display: flex;
  align-items: baseline;
`;

const PreviewCheckmark = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #cdd6e5;
  background-color: #094ec3;
  border-radius: 10px;
  margin-right: 6px;
`;

const PreviewTranslatedPageCount = styled.p`
  font-size: 14px;
`;

const PreviewAbstractTitle = styled.p`
  font-size: 24px;
  line-height: 130%;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    max-height: 128px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const PreviewAbstractSubtitle = styled.p`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
`;

const PreviewAbstract = styled.p<{ hide?: boolean }>`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;

  &.hide {
    height: 100px;
    overflow: hidden;
    position: relative;
  }

  &.hide:after {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 100%;
    width: 100%;
    content: '';
    background: linear-gradient(to top, #f4f8ff 5%, transparent 100%);
  }
`;

const PreviewSeeMore = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  color: #0954d3;
  cursor: pointer;
`;

const PremiumCTAContainer = styled.div`
  padding: 0 50px 20px 50px;
  border-radius: 0 0 4px 4px;
  display: flex;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    padding: 10px 16px 20px 16px;
  }
`;

const PreviewPDFImage = styled.img`
  width: 130px;
  height: 168px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    width: 100px;
    height: 130px;
  }
`;

interface Props {
  onClose: () => void;
  work: Work;
  premium: boolean;
  userLanguages: LanguageCode[];
  source:
    | 'swp-sticky-header-translate-button'
    | 'swp-translate-button'
    | 'loswp-translate-button'
    | 'library-translate-button';
}

interface State {
  bucket: ReturnType<typeof translationModalUpsellAbTest>;
  showUpsell: boolean;
  sourceLanguage?: LanguageCode;
  targetLanguage?: LanguageCode;
  translatedTitle?: string;
  translatedAbstract?: string;
  thumbnailUrl?: string;
  numberOfPages?: number;
  previewSuccess: boolean;
  hideAbstract: boolean;
  abstractHeading?: string;
  premiumDownloadStarted: boolean;
}

function workIdAsInt(workId: string | number): number {
  return typeof workId === 'string' ? parseInt(workId) : workId;
}

class TranslationModal extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { work, userLanguages, premium } = props;

    const bucket = translationModalUpsellAbTest();

    const sourceLanguage =
      work.language != null ? (work.language as LanguageCode) : undefined;
    const targetLanguage =
      work.language === userLanguages[0] ? undefined : userLanguages[0];

    this.state = {
      bucket: bucket,
      showUpsell: !premium && !!(sourceLanguage && targetLanguage),
      sourceLanguage,
      targetLanguage,
      previewSuccess: false,
      hideAbstract: true,
      premiumDownloadStarted: false,
    };
  }

  componentDidMount(): void {
    this.state.showUpsell && this.handlePreview();
  }

  setSourceLanguage = (language: LanguageCode): void => {
    if (this.props.premium) {
      this.setState({ sourceLanguage: language });
    } else {
      this.setState({ sourceLanguage: language, showUpsell: true }, () => {
        if (language) {
          this.handlePreview();
        }
      });
    }
  };

  handlePreview(): void {
    const { sourceLanguage, targetLanguage, bucket } = this.state;
    if (!sourceLanguage || !targetLanguage) {
      return;
    }

    const promise = getPreview(
      workIdAsInt(this.props.work.id),
      sourceLanguage,
      targetLanguage,
    );
    promise.then(
      ({
        title,
        abstract,
        abstract_heading,
        thumbnail_url,
        number_of_pages,
      }: PreviewResponse) => {
        this.setState({
          translatedTitle: title,
          translatedAbstract: abstract,
          abstractHeading: abstract_heading,
          thumbnailUrl: thumbnail_url,
          numberOfPages: number_of_pages,
          previewSuccess: !!abstract || bucket !== 'with_abstract_preview',
        });
      },
    );
  }

  setTargetLanguage = (language: LanguageCode): void => {
    if (this.props.premium) {
      this.setState({ targetLanguage: language });
    } else {
      this.setState({ targetLanguage: language, showUpsell: true }, () => {
        if (language) {
          this.handlePreview();
        }
      });
    }
  };

  onTranslateButtonClicked = () => {
    const { sourceLanguage, targetLanguage } = this.state;
    if (!sourceLanguage || !targetLanguage) {
      return;
    }

    if (this.props.premium) {
      trackClick({
        source: 'premium-download-translated-pdf',
        entityId: this.props.work.id,
        extra: {
          source_language: sourceLanguage,
          target_language: targetLanguage,
        },
      });

      if (!this.state.premiumDownloadStarted) {
        this.setState({ premiumDownloadStarted: true }, () => {
          downloadTranslation(
            workIdAsInt(this.props.work.id),
            sourceLanguage,
            targetLanguage,
          );
        });
      } else {
        return;
      }
    } else {
      navigateToCheckout(this.props.source, this.props.work.id);
    }
  };

  onSeeMoreClicked = () => {
    this.setState(({ hideAbstract, ...state }) => ({
      hideAbstract: !hideAbstract,
      ...state,
    }));
  };

  render(): React.ReactElement {
    const { onClose, work, premium } = this.props;
    const {
      sourceLanguage,
      targetLanguage,
      showUpsell,
      bucket,
      previewSuccess,
      translatedTitle,
      translatedAbstract,
      thumbnailUrl,
      numberOfPages,
      hideAbstract,
      abstractHeading,
    } = this.state;

    return (
      <Modal onClose={onClose}>
        <ImpressionWatcher
          source="swp-translation-modal"
          entity_type={2}
          entity_id={workIdAsInt(work.id)}
          extra={{ bucket: bucket }}
        >
          <X className="fa fa-times" onClick={onClose} />
          <TopSelectionContainer button={showUpsell || premium}>
            <Header>
              <img
                src={$image_url('translation/translation-icon.svg')}
                style={{ marginRight: 8, width: 32 }}
              />
              <Title>Translate</Title>
            </Header>
            <Body>
              <LeftSelectionContainer>
                <LanguageDropdown
                  selectedLanguage={sourceLanguage}
                  onSelect={this.setSourceLanguage}
                />
                <SubText>Auto-Detected</SubText>
              </LeftSelectionContainer>
              <IconContainer>
                <Icon name="arrow_forward" />
              </IconContainer>
              <RightSelectionContainer>
                <LanguageDropdown
                  selectedLanguage={targetLanguage}
                  onSelect={this.setTargetLanguage}
                />
              </RightSelectionContainer>
            </Body>
          </TopSelectionContainer>

          {premium && (
            <PremiumCTAContainer>
              <TranslateButton
                longButtonText="Download Translated PDF"
                shortButtonText="Download Translated PDF"
                onClick={this.onTranslateButtonClicked}
                clickable={!!(sourceLanguage && targetLanguage)}
                color={'blue'}
              />
            </PremiumCTAContainer>
          )}

          {showUpsell && bucket === 'with_abstract_preview' && (
            <UpsellContainer>
              {previewSuccess && (
                <PreviewAbstractTextContainer>
                  <PreviewAbstractTitle>{translatedTitle}</PreviewAbstractTitle>
                  <PreviewAbstractSubtitle>
                    {abstractHeading || 'Abstract'}
                  </PreviewAbstractSubtitle>
                  <PreviewAbstract hide={hideAbstract}>
                    {translatedAbstract}
                  </PreviewAbstract>
                  <PreviewSeeMore onClick={this.onSeeMoreClicked}>
                    {hideAbstract ? 'See More' : 'See Less'}{' '}
                    <i
                      className={
                        'fa fa-chevron-' + (hideAbstract ? 'down' : 'up')
                      }
                      aria-hidden="true"
                    />
                  </PreviewSeeMore>
                </PreviewAbstractTextContainer>
              )}
              <TranslateButton
                longButtonText="Upgrade to Download Translated PDF"
                shortButtonText="Upgrade to Download Translation"
                onClick={this.onTranslateButtonClicked}
                clickable={true}
              />
              <TranslationFeaturesText withPreview={false} />
            </UpsellContainer>
          )}

          {showUpsell && bucket === 'with_preview' && (
            <PreviewUpsellContainer>
              {previewSuccess && (
                <PreviewContainer>
                  <PreviewPDFImageContainer>
                    <PreviewPDFImage src={thumbnailUrl} />
                    <PreviewFlag>
                      <PreviewFlagImage
                        src={$image_url(
                          `translation/flags/${targetLanguage}.jpg`,
                        )}
                      />
                    </PreviewFlag>
                  </PreviewPDFImageContainer>
                  <PreviewTextContainer>
                    <PreviewTitle>{translatedTitle}</PreviewTitle>
                    <PreviewTranslatedPageCountLine>
                      <PreviewCheckmark>
                        <Icon name="check" size={12} />
                      </PreviewCheckmark>
                      {numberOfPages && (
                        <PreviewTranslatedPageCount>
                          {`${numberOfPages} ${pluralize('page', numberOfPages)} translated`}
                        </PreviewTranslatedPageCount>
                      )}
                    </PreviewTranslatedPageCountLine>
                  </PreviewTextContainer>
                </PreviewContainer>
              )}

              <TranslateButton
                longButtonText="Upgrade to Download Translated PDF"
                shortButtonText="Upgrade to Download Translation"
                onClick={this.onTranslateButtonClicked}
                clickable={true}
              />
              <TranslationFeaturesText withPreview={true} />
            </PreviewUpsellContainer>
          )}
        </ImpressionWatcher>
      </Modal>
    );
  }
}

export default TranslationModal;
